import type { Layout } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components/RibbonControlGroupMLR';
import {
    multiRow,
    sections,
    singleRow,
    stack,
} from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/components/RibbonControlGroupMLR/layoutTypings';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getComposeRibbonId } from 'owa-compose-ribbon-utils/lib/utils/getComposeRibbonId';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { getComposeViewState } from 'owa-mail-reading-pane-store/lib/utils/getComposeViewState';
import type { RibbonGroupId } from 'owa-ribbon-ids';
import {
    ActionControlId,
    FormatControlId,
    MailRibbonControlId,
    MailRibbonGroupId,
} from 'owa-ribbon-ids';

export const getMLRM3MessageTabGroupLayouts = computedFn(
    (group: RibbonGroupId, popoutComposeViewState?: ComposeViewState): Layout[] | undefined => {
        const viewState: ComposeViewState | undefined =
            popoutComposeViewState ?? getComposeViewState();
        switch (group) {
            case MailRibbonGroupId.Group_Clipboard_M3Message:
                return [
                    sections(
                        singleRow(
                            getComposeRibbonId(FormatControlId.PasteCutCopy, viewState).toString(),
                            stack(
                                getComposeRibbonId(FormatControlId.UndoRedo, viewState).toString(),
                                getComposeRibbonId(
                                    FormatControlId.FormatPainter,
                                    viewState
                                ).toString()
                            )
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_BasicText_M3Message:
                return [
                    sections(
                        multiRow(
                            [
                                getComposeRibbonId(
                                    FormatControlId.FontNamePicker,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(
                                    FormatControlId.FontSizePicker,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(FormatControlId.Bullets, viewState).toString(),
                                getComposeRibbonId(FormatControlId.Numbering, viewState).toString(),
                                getComposeRibbonId(
                                    FormatControlId.ChangeCase,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(
                                    FormatControlId.RemoveFormat,
                                    viewState
                                ).toString(),
                            ],
                            [
                                getComposeRibbonId(FormatControlId.Bold, viewState).toString(),
                                getComposeRibbonId(FormatControlId.Italic, viewState).toString(),
                                getComposeRibbonId(FormatControlId.Underline, viewState).toString(),
                                getComposeRibbonId(
                                    FormatControlId.StrikeThrough,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(
                                    FormatControlId.FontBackColorPicker,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(
                                    FormatControlId.FontColorPicker,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(FormatControlId.Outdent, viewState).toString(),
                                getComposeRibbonId(FormatControlId.Indent, viewState).toString(),
                            ]
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_Include_M3Message:
                return [
                    sections(
                        singleRow(
                            stack(
                                getComposeRibbonId(
                                    MailRibbonControlId.AttachFile,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(
                                    MailRibbonControlId.ShowLinkDialog,
                                    viewState
                                ).toString(),
                                getComposeRibbonId(
                                    MailRibbonControlId.AttachSig,
                                    viewState
                                ).toString()
                            )
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_AddIns_M3Message:
                return [
                    sections(
                        singleRow(
                            getComposeRibbonId(ActionControlId.GetAddIns, viewState).toString()
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_Tags_M3Message:
                return [
                    sections(
                        singleRow(
                            getComposeRibbonId(ActionControlId.SetImportance, viewState).toString(),
                            getComposeRibbonId(ActionControlId.Sensitivity, viewState).toString()
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_Save_M3Message:
                return [
                    sections(
                        singleRow(
                            getComposeRibbonId(MailRibbonControlId.Save, viewState).toString()
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_FindTime_M3Message:
                return [
                    sections(
                        singleRow(
                            getComposeRibbonId(ActionControlId.FindTime, viewState).toString()
                        )
                    ),
                ];

            case MailRibbonGroupId.Group_Proofing:
                return [
                    sections(
                        singleRow(
                            getComposeRibbonId(
                                ActionControlId.ProofingOptions,
                                viewState
                            ).toString(),
                            getComposeRibbonId(
                                ActionControlId.CheckForAccessibilityIssues,
                                viewState
                            ).toString()
                        )
                    ),
                ];

            default:
                return undefined;
        }
    }
);
